import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Calendar, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
}).format(amount || 0);

const methodOrder = ['cash', 'upi', 'bank', 'cheque', 'card', 'other'];

export default function ReportsFinancialPage() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getFinancialSummary(period);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch financial report');
    } finally {
      setLoading(false);
    }
  };

  const flowChart = useMemo(() => {
    const inflow = data?.payment_inflow_breakdown || {};
    const outflow = data?.payment_outflow_breakdown || {};
    return methodOrder.map((method) => ({
      method: method.toUpperCase(),
      inflow: Number(inflow[method] || 0),
      outflow: Number(outflow[method] || 0),
    }));
  }, [data]);

  const profitChart = useMemo(
    () => (data?.sales_profit_by_product_chart || []).slice(0, 10).map((row) => ({
      name: row.product_name,
      profit: Number(row.total_profit || 0),
    })),
    [data]
  );

  const downloadExcel = async () => {
    try {
      const res = await adminApi.downloadAdminDatasetExcel('financial-profit', { period });
      downloadBlob(res.data, `financial-profit-${period}.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-financial-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · Financial Overview</h1>
          <p className="text-[#A8A8A8] mt-1">Cash/Account position, receivables, payables, and profit insights</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadExcel}>
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 border-[#E5E5E5]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Cash in Hand</p><p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(data?.cash_in_hand)}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">In Account</p><p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(data?.in_account)}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Payment to be Received</p><p className="text-2xl font-bold text-[#C97A5E]">{formatCurrency(data?.payment_to_be_received)}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Payment to be Done</p><p className="text-2xl font-bold text-[#C97A5E]">{formatCurrency(data?.payment_to_be_done)}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Sales Profit (Total)</p><p className="text-2xl font-bold text-[#4A7C59]">{formatCurrency(data?.sales_profit_total)}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Profit Margin</p><p className="text-2xl font-bold text-[#4A7C59]">{Number(data?.sales_profit_margin_percentage || 0).toFixed(2)}%</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Payment Inflow vs Outflow</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={flowChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="method" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="inflow" fill="#4A7C59" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outflow" fill="#C97A5E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Top Product Profit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={profitChart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="profit" fill="#5C4033" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Sales Profit by Product</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Product</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pieces</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Revenue</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Estimated Cost</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Profit</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Margin %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {(data?.sales_profit_by_product || []).map((row) => (
                  <tr key={row.product_id} className="hover:bg-[#FAFAF9]/50">
                    <td className="py-3 px-4 text-[#2D2420] font-medium">{row.product_name}</td>
                    <td className="py-3 px-4 text-right text-[#2D2420]">{Number(row.total_pieces || 0).toFixed(0)}</td>
                    <td className="py-3 px-4 text-right text-[#2D2420]">{formatCurrency(row.total_revenue)}</td>
                    <td className="py-3 px-4 text-right text-[#2D2420]">{formatCurrency(row.total_cost)}</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#4A7C59]">{formatCurrency(row.total_profit)}</td>
                    <td className="py-3 px-4 text-right text-[#2D2420]">{Number(row.margin_percentage || 0).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
